import {
  Badge,
  BadgeProps,
  Box,
  Separator,
  Drawer,
  Flex,
  Grid,
  HStack,
  IconButton,
  Link,
  HoverCard,
  StackProps,
  VStack,
  IconProps,
  BoxProps,
} from '@chakra-ui/react'
import { ColorModeButton } from '@src/components/chakra-ui/color-mode'
import { useAdvisorsAttention } from '@src/components/sco/Advisors/hooks/useAdvisorsAttention'
import { ButtonLink, ButtonLinkProps } from '@src/components/sco/ButtonLink'
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList'
import { StartupscomLogo } from '@src/components/sco/StartupscomLogo'
import { primaryNavItems } from '@src/components/sco/nav/ScoSiteNavBar/primaryNavItems'
import { secondaryNavItems } from '@src/components/sco/nav/ScoSiteNavBar/secondaryNavItems'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { LuMenu, LuX } from 'react-icons/lu'

export type ScoSiteNavBarProps = {
  boxProps?: BoxProps
  centerLogo?: boolean
  conversionMode?: boolean
  hideMobileMenu?: boolean
  logoHref?: string
  primaryNavItems?: Array<SiteNavItem>
  secondaryNavItems?: Array<SiteNavItem>
}

export const siteNavBarHeight = [60, 60, 60, 68]
export const siteNavBarHeightPx = siteNavBarHeight.map(x => `${x}px`)

export const ScoSiteNavBar = (props: ScoSiteNavBarProps) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { currentUser } = useCurrentUser()
  const [primaryNavDrawerIsOpen, setPrimaryNavDrawerIsOpen] = useState(false)
  const { getBreakpointValue, activeContainerBreakpoint } = useBreakpointContext()
  // Shared W3 payload; the Chat icon reads the same cached query.
  const { total: advisorsAttentionCount } = useAdvisorsAttention()

  const fullUri = `${pathname}${`${searchParams}`.length > 0 ? '?' : ''}${searchParams}`

  const primaryItems = useMemo(() => {
    return (
      props.primaryNavItems ||
      primaryNavItems({
        currentUser,
        pathname: fullUri,
        getBreakpointValue,
        advisorsAttentionCount,
      })
    )
  }, [advisorsAttentionCount, currentUser, fullUri, getBreakpointValue, props.primaryNavItems])

  const secondaryItems = useMemo(() => {
    return (
      props.secondaryNavItems ||
      secondaryNavItems({
        currentUser,
        pathname: fullUri,
        getBreakpointValue,
      })
    )
  }, [currentUser, fullUri, activeContainerBreakpoint, getBreakpointValue, props.secondaryNavItems])

  return (
    <>
      <Box
        as={'nav'}
        backdropFilter={'blur(20px)'}
        bgColor={'colorPalette.bg/70'}
        height={siteNavBarHeightPx}
        position={'fixed'}
        top={0}
        width={'full'}
        zIndex={'sticky'}
        {...props.boxProps}
      >
        <Grid gridTemplateColumns={'1fr auto 1fr'} height={siteNavBarHeightPx} top={0} width={'full'}>
          <HStack gap={0} justifySelf={'start'}>
            {!props.hideMobileMenu ? (
              <IconButton
                aria-label={'Open Site Navigation Menu'}
                as={'a'}
                size={'md'}
                variant={'ghost'}
                onClick={() => setPrimaryNavDrawerIsOpen(!primaryNavDrawerIsOpen)}
              >
                <LuMenu />
              </IconButton>
            ) : (
              <Box width={3} />
            )}
            {!props.centerLogo && <StartupscomLogo href={props.logoHref} />}
          </HStack>

          <Flex
            align={'center'}
            flexGrow={1}
            justify={props.centerLogo ? 'center' : 'flex-start'}
            paddingX={{ base: 0, lg: '0.75em' }}
          >
            {props.centerLogo ? (
              <StartupscomLogo href={props.logoHref} />
            ) : (
              !props.conversionMode &&
              getBreakpointValue({ base: false, lg: true }) && <NavItems items={primaryItems} />
            )}
          </Flex>
          <Flex align={'stretch'} justify={'center'} justifySelf={'right'} paddingX={'0.75em'}>
            {!props.conversionMode ? (
              <NavItems gap={['0.5em', '0.5em', '0.75em']} items={secondaryItems} />
            ) : (
              <NavItems
                gap={['0.5em', '0.5em', '0.75em']}
                items={[
                  currentUser?.isRegistered
                    ? secondaryItems[secondaryItems.length - 1]
                    : secondaryItems[secondaryItems.length - 2],
                ]}
              />
            )}
          </Flex>
        </Grid>
        {!props.hideMobileMenu && (
          <NavDrawer
            drawerIsOpen={primaryNavDrawerIsOpen}
            drawerItems={[...primaryItems, ...secondaryItems].filter(x => !x.dividerProps)}
            drawerOnClose={() => setPrimaryNavDrawerIsOpen(false)}
          />
        )}
      </Box>
      <Box height={siteNavBarHeight.map(heightInPx => `${heightInPx}px`)} />
    </>
  )
}

export type SiteNavItem = {
  activePaths?: Array<string>
  badgeProps?: BadgeProps
  buttonProps?: ButtonLinkProps
  customRender?: () => ReactNode
  dividerProps?: any
  hideOnMobile?: boolean
  href?: string
  iconProps?: IconProps
  items?: Array<SiteNavItem>
  linkProps?: ButtonLinkProps
  render?: () => ReactNode
}

export const NavItems = (props: { items: Array<SiteNavItem>; gap?: StackProps['gap'] }) => {
  const { getBreakpointValue } = useBreakpointContext()

  return (
    <>
      <HStack
        align={'stretch'}
        gap={props.gap || getBreakpointValue(['0.25em', '0.25em', '0.5em', '0.75em', '1.33em'])}
        height={'full'}
        justify={'stretch'}
      >
        {props.items.map((item, idx) => (
          <NavItem key={idx} {...item} />
        ))}
      </HStack>
    </>
  )
}

export const NavItem = (props: SiteNavItem) => {
  const pathname = usePathname()
  const { getBreakpointValue } = useBreakpointContext()
  const isMobile = getBreakpointValue({ base: true, sm: false })

  if (props.render) {
    return props.render()
  }

  const isActive = [props.href, ...(props.activePaths || [])]
    .filter(Boolean)
    .find(p => (p && p.length > 1 ? pathname?.startsWith(p) : pathname == p))

  let children = null

  if (props.hideOnMobile && isMobile) {
    return <></>
  }

  if (props.dividerProps) {
    return <Separator alignSelf={'center'} height={'2em'} orientation={'vertical'} {...props.dividerProps} />
  }

  if (props.buttonProps) {
    children = (
      <ButtonLink alignSelf={'center'} href={props.buttonProps?.href || '#'} {...props.buttonProps}>
        {props.buttonProps.children || props.buttonProps.title || 'Item'}
      </ButtonLink>
    )
  }

  if (props.linkProps) {
    children = (
      <Link
        alignItems={'center'}
        alignSelf={'stretch'}
        as={NextLink}
        borderBottom={'0.33em solid'}
        borderBottomColor={isActive ? 'action.active' : 'transparent'}
        // color={isActive ? 'orange.400' : 'colorPalette.fg'}
        // colorPalette={isActive ? 'orange' : 'gray'}
        borderRadius={'none'}
        display={'flex'}
        fontFamily={'Poppins, sans-serif'}
        fontSize={getBreakpointValue(['10px', '10px', '10px', '11px', '12px'])}
        fontWeight={900}
        justifyContent={'center'}
        justifySelf={'stretch'}
        letterSpacing={'1.5px'}
        lineHeight={1}
        paddingLeft={'1.5px'}
        paddingTop={'0.33em'}
        textDecoration={'none'}
        textTransform={'uppercase'}
        _active={{
          borderBottomColor: 'colorPalette.400',
        }}
        _hover={{
          borderBottomColor: 'colorPalette.400',
          color: 'colorPalette.fg.default',
        }}
        color={{
          base: isActive ? 'orange.600' : 'colorPalette.fg',
          _dark: isActive ? 'orange.400' : 'colorPalette.fg',
        }}
        {...props.linkProps}
        href={props.linkProps?.href || '#'}
      >
        <span>{props.linkProps.children || props.linkProps.title}</span>
      </Link>
    )
  }

  return (
    <>
      <HoverCard.Root closeDelay={200} openDelay={0}>
        <HoverCard.Trigger asChild>
          <Flex height={'full'} position={'relative'}>
            {children}
            {props.badgeProps && (
              <Badge
                marginRight={'0.5em'}
                position={'absolute'}
                right={'-1.5em'}
                top={'0.75em'}
                {...props.badgeProps}
              >
                {props.badgeProps.children || props.badgeProps.title}
              </Badge>
            )}
          </Flex>
        </HoverCard.Trigger>
        {props.items && (
          <HoverCard.Positioner>
            <HoverCard.Content
              borderColor={'colorPalette.border/50'}
              maxWidth={'400px'}
              padding={0}
              width={'auto'}
            >
              <VStack borderRadius={'md'} gap={'2'} padding={2}>
                {props.items.map((item, idx) => {
                  const isSubitemActive = !!(item.activePaths || [item.href]).find(
                    subitemPath => subitemPath && subitemPath.length > 0 && pathname?.startsWith(subitemPath)
                  )

                  if (item.customRender) {
                    return <Box key={idx}>{item.customRender()}</Box>
                  }

                  if (item.dividerProps) {
                    return <Separator key={idx} {...item.dividerProps} />
                  }

                  if (item.linkProps) {
                    return (
                      <Box key={idx} width={'full'}>
                        <ButtonLink
                          fontWeight={'bolder'}
                          padding={4}
                          size={'md'}
                          textDecoration={'none'}
                          textTransform={'uppercase'}
                          variant={'ghost'}
                          width={'full'}
                          {...item.linkProps}
                        >
                          {item.linkProps?.children || item.linkProps?.title || 'Item'}
                          {item.badgeProps && (
                            <Badge
                              as={'span'}
                              {...item.badgeProps}
                              color={'white'}
                              marginLeft={'1em'}
                              marginTop={'-0.25em'}
                            >
                              {item.badgeProps.children || item.badgeProps.title}
                            </Badge>
                          )}
                        </ButtonLink>
                      </Box>
                    )
                  }
                })}
              </VStack>
            </HoverCard.Content>
          </HoverCard.Positioner>
        )}
      </HoverCard.Root>
    </>
  )
}

export const NavDrawer = (props: {
  drawerIsOpen?: boolean
  drawerItems?: Array<SiteNavItem>
  drawerOnClose?: () => any
}) => {
  const pathname = usePathname()

  useEffect(() => {
    if (props.drawerOnClose) {
      props.drawerOnClose()
    }
  }, [pathname])

  const mapToSidebarItem = (item: SiteNavItem): SidebarNestedNavListSection => {
    const parentHref = item.linkProps?.href || item.buttonProps?.href
    const parentTitle = item.linkProps?.title || item.buttonProps?.title

    return {
      activePaths: item.activePaths,
      // Primary link items keep their desktop count (Advisors your-turn total); children stay unbadged.
      badgeProps: item.linkProps ? item.badgeProps : undefined,
      title: parentTitle,
      // Next.js Link requires a string; accordion parents with expand-only still need a valid href.
      href: parentHref || '#',
      customRender: item.customRender || item.render,
      items: item.items
        ? ([
            // Only inject the parent "overview" row when it has a real destination.
            ...(parentHref
              ? [
                  {
                    title: parentTitle,
                    href: parentHref,
                    activePaths: [parentHref].filter(Boolean),
                    activeExact: true,
                  },
                ]
              : []),
            ...item.items.map(subItem => ({
              title: subItem.linkProps?.title || subItem.buttonProps?.title,
              href: subItem.linkProps?.href || subItem.buttonProps?.href || '#',
              activePaths: [subItem.linkProps?.href, subItem.buttonProps?.href].filter(Boolean),
              activeExact: true,
              dividerProps: subItem.dividerProps ? {} : null,
              customRender: subItem.customRender || subItem.render,
            })),
          ] as SidebarNestedNavListSection['items'])
        : null,
    }
  }

  return (
    <Drawer.Root open={props.drawerIsOpen} placement={'start'} onOpenChange={props.drawerOnClose}>
      <Drawer.Backdrop />
      <Drawer.Positioner />
      <Drawer.Content fontSize={'md'} minHeight={'100vh'} position={'absolute'} top={0}>
        <Drawer.Body padding={4}>
          <HStack justify={'space-between'} marginBottom={4} width={'full'}>
            <StartupscomLogo />
            <HStack gap={2}>
              <ColorModeButton size={'sm'} />
              <IconButton
                aria-label={'Close'}
                borderRadius={'md'}
                variant={'outline'}
                onClick={props.drawerOnClose}
              >
                <LuX />
              </IconButton>
            </HStack>
          </HStack>
          <SidebarNestedNavList
            accordionBehavior={'expand-only'}
            sections={props.drawerItems.map(item => mapToSidebarItem(item))}
          />
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  )
}
